import { useId, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { useLayerFocus } from "./focus";

/**
 * The one dialog. Centered on wide screens, a bottom sheet on phones
 * (styles.css `.modal`). Escape and the backdrop close it unless
 * `dismissible` is false (e.g. while a save is running); focus stays inside
 * and returns where it was on close.
 */
export function Modal({
  title,
  subtitle,
  children,
  onClose,
  dismissible = true,
  size,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  dismissible?: boolean;
  /** "wide" for tables and side-by-side review. */
  size?: "wide";
}) {
  const ref = useRef<HTMLElement>(null);
  const titleId = useId(),
    subtitleId = useId();
  useLayerFocus(ref, onClose, { dismissible });
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <section
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        className={`modal${size === "wide" ? " modal-wide" : ""}`}
        tabIndex={-1}
      >
        <header>
          <div>
            <h2 id={titleId}>{title}</h2>
            {subtitle && <p id={subtitleId}>{subtitle}</p>}
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close dialog" disabled={!dismissible}>
            <X size={20} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
